import React from 'react'
import {Link as ReactRouterLink} from 'react-router-dom'
import {
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '../../../utils/chakraMonomorphic'
import {RouterLink as Link} from '../../../utils/chakraRouterLink'

import {PersonCloseCall} from '../../../types'
import {CoverageState, isConclusive} from '../coverageState'
import copy from '../copy.json'
import {formatPercent, formatTimestamp, resolveTaggerName} from '../formatters'
import {PhotoStrip} from './PhotoStrip'
import {buildAlbumPhotoUrl, isLinkableId} from '../../../utils/photoDeepLink'

interface Props {
  heading: string
  description: string
  calls: PersonCloseCall[]
  nameMap: Map<number, string>
  testId: string
  clientId: number
  /**
   * What the recognition data can support. Only `full` licenses presenting an
   * empty list as a finding; every other state falls back to this table's own
   * inconclusive wording, with the explanation rendered once by CoverageNote.
   */
  coverageState: CoverageState
  /**
   * Shown when the list is empty AND coverage is conclusive — the real finding.
   */
  emptyMessage: string
  /**
   * Shown when the list is empty and coverage is NOT conclusive. Per table on
   * purpose: the two tables ask opposite questions ("was recognition beaten?"
   * vs "did recognition come close?"), so neither can borrow the other's
   * wording. Sharing one string previously rendered accepted-table vocabulary
   * under the near-miss heading and made nearMissEmpty unreachable.
   */
  inconclusiveMessage: string
  /** See PhotoStrip — false suppresses album links for a cross-camp lookup. */
  canLinkAlbums: boolean
}

export const CloseCallsTable: React.FC<Props> = ({
  heading,
  description,
  emptyMessage,
  calls,
  nameMap,
  testId,
  clientId,
  coverageState,
  inconclusiveMessage,
  canLinkAlbums,
}): React.ReactElement => (
  <Box mb={8} data-testid={testId}>
    <Text fontSize='1.05rem' fontWeight='bold'>
      {heading}
    </Text>
    <Text fontSize='sm' color='gray.700' mb={2}>
      {description}
    </Text>

    {calls.length === 0 ? (
      <Text fontSize='sm' color='gray.700' data-testid={`${testId}-empty`}>
        {isConclusive(coverageState) ? emptyMessage : inconclusiveMessage}
      </Text>
    ) : (
      <>
        {/* Thumbnails follow the table's own ordering (most confident first),
            not photo-id recency — for a close call, "which one did recognition
            come closest on" is the useful ordering.

            `calls` is passed straight through: PersonCloseCall is a structural
            superset of PersonTaggedPhoto, so re-mapping the three shared fields
            only created a second place for them to drift. */}
        <PhotoStrip
          photos={calls}
          clientId={clientId}
          testId={`${testId}-photos`}
          canLinkAlbums={canLinkAlbums}
        />
        <Table size='sm' variant='simple' mt={2}>
          <Thead>
            <Tr>
              <Th>{copy.closeCalls.columns.photo}</Th>
              <Th>{copy.closeCalls.columns.album}</Th>
              <Th>{copy.closeCalls.columns.confidence}</Th>
              <Th>{copy.closeCalls.columns.threshold}</Th>
              <Th>{copy.closeCalls.columns.attempted}</Th>
              <Th>{copy.closeCalls.columns.taggedBy}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {calls.map((call) => (
              <Tr key={call.photoId}>
                <Td>
                  {canLinkAlbums && isLinkableId(call.albumId) ? (
                    <Link
                      as={ReactRouterLink}
                      to={buildAlbumPhotoUrl(call.albumId, call.photoId)}
                      data-testid={`${testId}-link-${call.photoId}`}
                      color='blue.600'
                      textDecoration='underline'>
                      {call.photoId}
                    </Link>
                  ) : (
                    // Plain text rather than a link that would land the operator
                    // on the wrong camp's album or on /album/0.
                    <Text
                      as='span'
                      data-testid={`${testId}-unlinked-${call.photoId}`}>
                      {call.photoId}
                    </Text>
                  )}
                </Td>
                <Td>{call.albumName}</Td>
                <Td>{formatPercent(call.confidence)}</Td>
                {/* Shown per row because per-camp acceptance-threshold
                  overrides mean this is not always 75%. */}
                <Td>{formatPercent(call.thresholdUsed)}</Td>
                <Td>{formatTimestamp(call.attemptedUTC)}</Td>
                <Td>
                  {resolveTaggerName(
                    call.taggingPersonId,
                    nameMap,
                    copy.closeCalls.unknownTagger,
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </>
    )}
  </Box>
)
